import { API_URL as DEFAULT_API_URL } from '../config/apiConfig';
import { COMBINED_FACTION_STRATEGIES } from './factionUiColors';
import { preloadOrderCardImages } from './preloadOrderCardImages';
import { preloadAllOrderBorderImages } from './borderImageCache';

/** Default Strategy faction on first visit — matches Strategy.js initial selectedFaction. */
export const STRATEGY_DEFAULT_FACTION = 'Remnants of Humanity';

/** Sentinel key for player1-only catalog: every order with DB availability Created */
export const DEV_CREATED_CATALOG_FACTION = '__DEV_CREATED_CATALOG__';

const ALL_STRATEGY_BASE_FACTIONS = Object.freeze([
  'Remnants of Humanity',
  'Exolot Dominion',
  'League of Shadows',
  "Ra's Children",
  'Asrah',
  'The Conspiracy',
  'The Corrupted Core',
  'The Covenant',
  'The Divine Syndicate',
  'The Forsaken Pact',
  'The Hollow',
  'The Iron Alliance',
  'The Last Crusade',
  'The Shadow Consortium',
  'The Silent Accord',
]);

/** @typedef {{ orders: object[] }} StrategyOrdersCatalogPayload */

const catalogByKey = new Map();
const inflightByKey = new Map();

/**
 * @param {{ selectedFaction: string, showSentients: boolean, language: string }} params
 * @returns {string}
 */
export function buildStrategyOrdersCatalogCacheKey({ selectedFaction, showSentients, language }) {
  if (!selectedFaction || typeof selectedFaction !== 'string') {
    throw new Error('buildStrategyOrdersCatalogCacheKey: selectedFaction is required');
  }
  const normalizedLanguage = (language || 'en-us').toLowerCase().replace('_', '-');
  return `${normalizedLanguage}|${selectedFaction}|${showSentients ? '1' : '0'}`;
}

/**
 * Fetch deduped order rows for a Strategy faction tab (API only, no media preload).
 * @param {{
 *   apiUrl?: string,
 *   token: string | null,
 *   selectedFaction: string,
 *   showSentients?: boolean,
 *   language?: string,
 * }} params
 * @returns {Promise<object[]>}
 */
export async function fetchStrategyOrdersCatalog({
  apiUrl = DEFAULT_API_URL,
  token,
  selectedFaction,
  showSentients = true,
  language,
}) {
  if (!apiUrl || typeof apiUrl !== 'string') {
    throw new Error('fetchStrategyOrdersCatalog: apiUrl is required');
  }
  if (!selectedFaction || typeof selectedFaction !== 'string') {
    throw new Error('fetchStrategyOrdersCatalog: selectedFaction is required');
  }

  const normalizedLanguage = (language || 'en-us').toLowerCase().replace('_', '-');

  let factionsToFetch;
  let filterCreatedOnly = false;
  if (selectedFaction === DEV_CREATED_CATALOG_FACTION) {
    filterCreatedOnly = true;
    factionsToFetch = [...ALL_STRATEGY_BASE_FACTIONS];
    if (showSentients && !factionsToFetch.includes('The Sentients')) {
      factionsToFetch.push('The Sentients');
    }
  } else {
    factionsToFetch = COMBINED_FACTION_STRATEGIES[selectedFaction] || [selectedFaction];
    if (showSentients && !factionsToFetch.includes('The Sentients')) {
      factionsToFetch = [...factionsToFetch, 'The Sentients'];
    }
  }

  const results = await Promise.all(
    factionsToFetch.map(async (faction) => {
      const response = await fetch(
        `${apiUrl}/orders?faction=${encodeURIComponent(faction)}&lang=${encodeURIComponent(normalizedLanguage)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(
          `fetchStrategyOrdersCatalog: /orders failed for ${faction} (${response.status})`
        );
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error(`fetchStrategyOrdersCatalog: unexpected response for ${faction}`);
      }
      return data;
    })
  );

  let allOrders = results.flat();
  if (filterCreatedOnly) {
    allOrders = allOrders.filter((o) => String(o.availability || '').toLowerCase() === 'created');
  }

  const uniqueOrders = [];
  const seenIds = new Set();
  for (const order of allOrders) {
    if (!seenIds.has(order.id)) {
      seenIds.add(order.id);
      uniqueOrders.push(order);
    }
  }

  uniqueOrders.sort((a, b) => {
    const normalize = (name) => (name ?? '').trim().toLowerCase();
    const aIsSentient = normalize(a.faction_name) === 'the sentients';
    const bIsSentient = normalize(b.faction_name) === 'the sentients';
    if (aIsSentient && !bIsSentient) return 1;
    if (!aIsSentient && bIsSentient) return -1;
    const costComparison = a.cost - b.cost;
    if (costComparison !== 0) return costComparison;
    return a.name.localeCompare(b.name);
  });

  return uniqueOrders;
}

/**
 * Fetch catalog + warm border/card images. Dedupes by faction/lang/sentients key.
 * @param {{
 *   apiUrl?: string,
 *   token: string | null,
 *   selectedFaction: string,
 *   showSentients?: boolean,
 *   language?: string,
 * }} params
 * @returns {Promise<StrategyOrdersCatalogPayload>}
 */
export function loadStrategyOrdersCatalogWithMedia(params) {
  const cacheKey = buildStrategyOrdersCatalogCacheKey(params);

  const cached = catalogByKey.get(cacheKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightByKey.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    const orders = await fetchStrategyOrdersCatalog(params);
    await Promise.all([
      preloadAllOrderBorderImages(),
      preloadOrderCardImages(orders),
    ]);
    /** @type {StrategyOrdersCatalogPayload} */
    const payload = { orders };
    catalogByKey.set(cacheKey, payload);
    inflightByKey.delete(cacheKey);
    return payload;
  })().catch((err) => {
    inflightByKey.delete(cacheKey);
    throw err;
  });

  inflightByKey.set(cacheKey, promise);
  return promise;
}

/**
 * Warm the default Strategy catalog while the player is on Welcome (or elsewhere).
 * Safe to call repeatedly — shares the same inflight promise / cache entry.
 * @param {Parameters<typeof loadStrategyOrdersCatalogWithMedia>[0]} params
 * @returns {Promise<StrategyOrdersCatalogPayload>}
 */
export function startStrategyOrdersCatalogPrefetch(params) {
  return loadStrategyOrdersCatalogWithMedia(params);
}

/**
 * @param {Parameters<typeof loadStrategyOrdersCatalogWithMedia>[0]} params
 * @returns {Promise<StrategyOrdersCatalogPayload>}
 */
export function awaitStrategyOrdersCatalogWithMedia(params) {
  return loadStrategyOrdersCatalogWithMedia(params);
}
